import { createContext, useState, useEffect, useContext, type ReactNode } from 'react';
import { IntlProvider, type MessageFormatElement, ReactIntlErrorCode } from 'react-intl';
import Cookies from 'js-cookie';

import { getParsedParams } from '@/utils/consts/params';

export const availableLocalizations = {
  UKRAINIAN: 'uk',
  ENGLISH: 'en',
  GERMAN: 'de',
  SPANISH: 'es',
  FRENCH: 'fr',
  PORTUGESE: 'pt',
} as const;
export const availableLocalizationsList = Object.values(availableLocalizations);

export type OneOfAvailableLocalizations = (typeof availableLocalizations)[keyof typeof availableLocalizations];

export const isValidLocalization = (value: string): value is OneOfAvailableLocalizations => {
  return (Object.values(availableLocalizations) as readonly string[]).includes(value);
};

const LOCALE_COOKIE_KEY = 'locale';
const saveLocaleToCookie = (locale: OneOfAvailableLocalizations) =>
  Cookies.set(LOCALE_COOKIE_KEY, locale, {
    domain: process.env.cookieDomain,
    path: '/',
  });

export const getLocaleFromCookie = (): OneOfAvailableLocalizations => {
  const cookieLocale = Cookies.get(LOCALE_COOKIE_KEY);

  if (cookieLocale && isValidLocalization(cookieLocale)) {
    return cookieLocale;
  }

  return availableLocalizations.ENGLISH;
};

const resolveLocale = (): OneOfAvailableLocalizations => {
  const cookieLocale = Cookies.get(LOCALE_COOKIE_KEY);

  const paramsLocale = getParsedParams().LANGUAGE;

  if (paramsLocale && isValidLocalization(paramsLocale)) {
    saveLocaleToCookie(paramsLocale);

    return paramsLocale;
  }

  if (cookieLocale && isValidLocalization(cookieLocale)) {
    return cookieLocale;
  }

  if (navigator.language.startsWith(availableLocalizations.UKRAINIAN)) {
    saveLocaleToCookie(availableLocalizations.UKRAINIAN);

    return availableLocalizations.UKRAINIAN;
  }
  saveLocaleToCookie(availableLocalizations.ENGLISH);

  return availableLocalizations.ENGLISH;
};

interface LocalizationContextState {
  selectedLocalization: OneOfAvailableLocalizations;
  availableLocalizations: typeof availableLocalizationsList;
  changeLocalization: (locale: OneOfAvailableLocalizations) => void;
}
const LocaleContext = createContext<LocalizationContextState>({
  selectedLocalization: availableLocalizations.ENGLISH,
  availableLocalizations: availableLocalizationsList,
  changeLocalization: () => {},
});

type Messages = Record<string, string> | Record<string, MessageFormatElement[]>;

const LocalizationProvider = ({ children }: { children: ReactNode }) => {
  const [selectedLocalization, setSelectedLocalization] = useState<OneOfAvailableLocalizations>(
    availableLocalizations.ENGLISH,
  );

  useEffect(() => {
    setSelectedLocalization(resolveLocale());
  }, []);

  const changeLocalization = (newLocale: OneOfAvailableLocalizations) => {
    setSelectedLocalization(newLocale);
    saveLocaleToCookie(newLocale);
  };

  const [messages, setMessages] = useState<Messages>({});

  useEffect(() => {
    import(`trans/${selectedLocalization}.json`)
      .then((localeModule) => {
        setMessages(localeModule as Messages);
      })
      .catch((err: unknown) => {
        console.error(`Error with loading "${selectedLocalization}": ${err}`);
      });
  }, [selectedLocalization]);

  return (
    <LocaleContext.Provider
      value={{ selectedLocalization, availableLocalizations: availableLocalizationsList, changeLocalization }}
    >
      <IntlProvider
        messages={messages}
        locale={selectedLocalization}
        defaultLocale={availableLocalizations.ENGLISH}
        onError={(err) => {
          if (err.code === ReactIntlErrorCode.MISSING_TRANSLATION) {
            return;
          }

          console.error(err);
        }}
      >
        {children}
      </IntlProvider>
    </LocaleContext.Provider>
  );
};

export default LocalizationProvider;

export const useLocale = () => useContext(LocaleContext);
